(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function getForm(root) {
    var formId = root.getAttribute('data-form-id');
    return formId ? document.getElementById(formId) : null;
  }

  /** The theme's add-to-cart button, which this module's button proxies. */
  function getNativeAddButton(root) {
    var form = getForm(root);
    return form ? form.querySelector('.product-form__submit, [type="submit"][name="add"]') : null;
  }

  /**
   * The theme's quantity input lives outside the module but is associated with
   * the product form via its `form` attribute, so look it up through the form
   * rather than by a section-specific id.
   */
  function getQuantityInput(root) {
    var form = getForm(root);
    var formId = root.getAttribute('data-form-id');

    var input =
      (formId && document.querySelector('input[name="quantity"][form="' + formId + '"]')) ||
      (form && form.querySelector('[name="quantity"]')) ||
      root.querySelector('[name="quantity"]');

    // If the quantity_selector block isn't present, own a hidden input so the
    // recommended bag count is still submitted.
    if (!input && formId) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      input.value = '1';
      input.setAttribute('form', formId);
      input.setAttribute('data-pwd-quantity', '');
      root.appendChild(input);
    }

    return input;
  }

  /**
   * Pre-fill the quantity without clicking or focusing anything. Clicking the
   * theme/app controls is what makes the page jump, so we only assign the value
   * and notify listeners.
   */
  function setQuantity(root, bags) {
    var qty = parseInt(bags, 10);
    if (!qty || qty < 1) return;

    var input = getQuantityInput(root);
    if (!input || String(input.value) === String(qty)) return;

    input.value = String(qty);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function activePanel(root) {
    return root.querySelector('[data-pwd-panel]:not([hidden])');
  }

  /* ------------------------------------------------------------------------ *
   * Recharge frequency sync
   * ------------------------------------------------------------------------ */

  var WIDGET_HOSTS = [
    'recharge-subscription-widget',
    '.recharge-subscription-widget',
    '[data-recharge-widget]',
  ];

  var syncToken = 0;

  var rootsCache = null;
  var rootsCachedAt = 0;

  /**
   * Every shadow root beneath the Recharge widget, plus the widget's own DOM.
   * Cached briefly: the walk touches every node in the widget and the purchase
   * poll asks for it repeatedly, so this keeps it off the main thread.
   */
  function collectRoots() {
    var now = (window.performance && performance.now()) || 0;
    if (rootsCache && now - rootsCachedAt < 1000) return rootsCache;

    var hosts = [];
    WIDGET_HOSTS.forEach(function (selector) {
      Array.prototype.slice.call(document.querySelectorAll(selector)).forEach(function (host) {
        if (hosts.indexOf(host) === -1) hosts.push(host);
      });
    });
    if (!hosts.length) {
      rootsCache = [];
      rootsCachedAt = now;
      return rootsCache;
    }

    var roots = [];
    hosts.forEach(function (host) {
      roots.push(host);
      if (host.shadowRoot) roots.push(host.shadowRoot);
    });

    // Breadth-first through nested shadow roots.
    for (var i = 0; i < roots.length; i++) {
      var all = roots[i].querySelectorAll('*');
      for (var j = 0; j < all.length; j++) {
        if (all[j].shadowRoot && roots.indexOf(all[j].shadowRoot) === -1) {
          roots.push(all[j].shadowRoot);
        }
      }
    }

    rootsCache = roots;
    rootsCachedAt = now;
    return roots;
  }

  function frequencyPattern(frequency) {
    var count = parseInt(String(frequency).split('-')[0], 10) || 1;
    if (count === 1) {
      return /monthly|per\s*month|every\s*month|(^|\D)1\s*month|4\s*week|30\s*day/i;
    }
    return new RegExp('every\\s*' + count + '\\s*month|' + count + '\\s*month', 'i');
  }

  /** Find the control representing this band's plan, best match first. */
  function findFrequencyControl(roots, ids, frequency) {
    var i;
    var selectors = [];

    ids.forEach(function (id) {
      if (!id) return;
      selectors.push(
        'input[value="' + id + '"]',
        'option[value="' + id + '"]',
        '[data-selling-plan-id="' + id + '"]',
        '[data-plan-id="' + id + '"]',
        '[data-value="' + id + '"]',
        '[value="' + id + '"]'
      );
    });

    // 1. Exact id match.
    if (selectors.length) {
      var query = selectors.join(',');
      for (i = 0; i < roots.length; i++) {
        var byId = roots[i].querySelector(query);
        if (byId) return byId;
      }
    }

    // 2. Id appearing in any attribute (covers custom attribute names).
    for (i = 0; i < roots.length; i++) {
      var candidates = roots[i].querySelectorAll('input,option,button,label,[role=button],[part]');
      for (var c = 0; c < candidates.length; c++) {
        var attributes = candidates[c].attributes;
        for (var a = 0; a < attributes.length; a++) {
          for (var k = 0; k < ids.length; k++) {
            if (ids[k] && attributes[a].value === String(ids[k])) return candidates[c];
          }
        }
      }
    }

    // 3. Fallback: match the rendered frequency text.
    if (frequency && frequency !== 'none') {
      var pattern = frequencyPattern(frequency);
      var exclude = parseInt(String(frequency).split('-')[0], 10) === 1 ? /2\s*month|3\s*month/i : null;
      for (i = 0; i < roots.length; i++) {
        var els = roots[i].querySelectorAll('button,[role=button],option,label,[part]');
        for (var e = 0; e < els.length; e++) {
          var el = els[e];
          var text = (el.textContent || '').trim();
          if (!text || el.children.length > 2) continue;
          if (pattern.test(text) && !(exclude && exclude.test(text))) return el;
        }
      }
    }

    return null;
  }

  /**
   * Hold the scroll position while the widget reacts. Recharge scrolls its own
   * controls into view, which is what made the original implementation jump;
   * correcting inside a capture-phase scroll listener and on each frame fixes it
   * before the browser paints, so there's no visible jitter. Any real user
   * scroll input releases the lock immediately.
   */
  function withScrollLock(callback) {
    var x = window.scrollX;
    var y = window.scrollY;
    var active = true;
    var frame;

    var release = function () {
      if (!active) return;
      active = false;
      window.removeEventListener('scroll', onScroll, true);
      ['wheel', 'touchstart', 'keydown'].forEach(function (type) {
        window.removeEventListener(type, release, true);
      });
      if (frame) cancelAnimationFrame(frame);
    };

    function onScroll() {
      if (active && (window.scrollX !== x || window.scrollY !== y)) window.scrollTo(x, y);
    }

    function tick() {
      if (!active) return;
      onScroll();
      frame = requestAnimationFrame(tick);
    }

    window.addEventListener('scroll', onScroll, true);
    ['wheel', 'touchstart', 'keydown'].forEach(function (type) {
      window.addEventListener(type, release, { capture: true, passive: true });
    });
    frame = requestAnimationFrame(tick);
    setTimeout(release, 600);

    try {
      callback();
    } catch (error) {
      release();
      throw error;
    }
  }

  /** Apply a selection to whatever kind of control we matched. */
  function applyControl(el) {
    if (el.tagName === 'OPTION') {
      var select = el.parentNode;
      if (select && select.tagName === 'SELECT') {
        if (select.value === el.value) return true;
        select.value = el.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }

    if (el.tagName === 'INPUT' && (el.type === 'radio' || el.type === 'checkbox')) {
      if (el.checked) return true;
      // Use a real click: Recharge only re-renders its "selected" frequency button on a
      // click. Setting .checked + dispatching change updated the form but left the old
      // frequency highlighted. .click() does not move focus, so it cannot scroll the page.
      el.click();
      return true;
    }

    // .click() does not move focus, so it cannot scroll the page by itself.
    el.click();
    return true;
  }

  /**
   * Backstop: make sure the value the form will actually submit matches the band,
   * even if the widget's own handler didn't run.
   */
  function alignFormSellingPlan(root, sellingPlanId) {
    if (!sellingPlanId) return;
    var form = getForm(root);
    var input = form && form.querySelector('[name="selling_plan"]');
    if (!input || String(input.value) === String(sellingPlanId)) return;
    input.value = String(sellingPlanId);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ------------------------------------------------------------------------ *
   * Cost per day follows the selected purchase option
   *
   * Each panel carries a pre-formatted cost for every purchase option (one-time
   * plus each subscription frequency), so switching between One-time and
   * Subscribe & save updates the figure without recomputing money formats in JS.
   * ------------------------------------------------------------------------ */

  function planIds(root) {
    return (root.getAttribute('data-plan-ids') || '').split(',').filter(Boolean);
  }

  var ONE_TIME_PATTERN = /one[\s-]?time|single\s*purchase|no\s*subscription/i;

  /** Label text for a control, including its wrapping label/container. */
  function controlLabel(el) {
    var text = (el.textContent || '').trim();
    if (!text) {
      var label = (el.closest && el.closest('label')) || el.parentElement;
      if (label) text = (label.textContent || '').trim();
    }
    return text.slice(0, 120);
  }

  // NB: the pseudo-class for a selected <option> is :checked, not :selected —
  // ':selected' is not valid CSS and makes querySelectorAll throw.
  var CHECKED_QUERY = 'input:checked,option:checked,[aria-checked="true"],[aria-selected="true"]';

  /** Every checked/selected control across the widget's shadow roots. */
  function checkedControls() {
    var found = [];
    collectRoots().forEach(function (r) {
      var checked;
      try {
        checked = r.querySelectorAll(CHECKED_QUERY);
      } catch (error) {
        return;
      }
      for (var c = 0; c < checked.length; c++) found.push(checked[c]);
    });
    return found;
  }

  /**
   * Which purchase option is active: a selling plan id, or 'onetime'.
   *
   * Order matters. The form's selling_plan input is authoritative when set. When
   * it's empty we must NOT simply look for a checked plan control, because
   * Recharge leaves the frequency radio checked inside the collapsed Subscribe
   * block even while One-time is selected — that's what made the cost per day
   * stick at the subscription figure. So an explicit One-time selection is
   * checked before any plan match.
   */
  function currentPurchaseKey(root) {
    var form = getForm(root);
    var input = form && form.querySelector('[name="selling_plan"]');
    if (input && input.value) return String(input.value);

    var controls = checkedControls();
    var i;

    // An explicitly selected One-time option beats a stale frequency radio.
    for (i = 0; i < controls.length; i++) {
      var value = String(controls[i].value || '');
      if (ONE_TIME_PATTERN.test(value) || ONE_TIME_PATTERN.test(controlLabel(controls[i]))) {
        return 'onetime';
      }
    }

    var ids = planIds(root);
    for (i = 0; i < controls.length; i++) {
      if (ids.indexOf(String(controls[i].value)) !== -1) return String(controls[i].value);
    }

    // A selling_plan input that exists but is empty means one-time was chosen.
    return 'onetime';
  }

  function refreshCosts(root) {
    var key = currentPurchaseKey(root);

    Array.prototype.slice.call(root.querySelectorAll('[data-pwd-panel]')).forEach(function (panel) {
      var target = panel.querySelector('[data-pwd-cost]');
      if (!target) return;

      var map = {};
      try {
        map = JSON.parse(panel.getAttribute('data-cost-map') || '{}');
      } catch (error) {
        console.warn('[weight-dosing] Could not read cost data for band ' + panel.getAttribute('data-band'));
      }

      // Per-plan figure when we have it, otherwise the plain one-time /
      // subscription pair — so a missing map key can't leave a stale price.
      var value =
        map[key] ||
        (key === 'onetime'
          ? panel.getAttribute('data-cost-onetime')
          : panel.getAttribute('data-cost-subscription'));

      if (!value) {
        console.warn('[weight-dosing] No cost available for purchase option "' + key + '".');
        return;
      }

      if (target.textContent.trim() !== value) target.textContent = value;
    });
  }

  /**
   * Recharge updates its selection internally, and doesn't reliably emit an event
   * we can hook, so poll the resolved purchase option. It's a single input read,
   * and it's paused whenever the tab is hidden.
   */
  function watchPurchaseOption(root) {
    var last = null;

    // Guarded so a single failure can't kill the interval and silently freeze
    // the price at whatever was rendered server-side.
    var check = function () {
      if (document.hidden) return;
      try {
        var key = currentPurchaseKey(root);
        if (key === last) return;
        last = key;
        refreshCosts(root);
      } catch (error) {
        console.warn('[weight-dosing] Could not resolve the purchase option:', error);
      }
    };

    check();
    setInterval(check, 400);
    // React immediately to a tap inside the widget, then let the poll confirm.
    document.addEventListener('change', check, true);
    document.addEventListener('click', function () {
      setTimeout(check, 100);
    }, true);
  }

  /**
   * Point the Recharge widget at this band's plan. The widget renders
   * asynchronously, so retry on a bounded schedule; a newer band selection
   * invalidates any in-flight attempt via the token.
   */
  function syncFrequency(root, detail, attempt, token) {
    if (!detail.frequency || detail.frequency === 'none') return;
    if (token !== syncToken) return;

    var ids = [detail.sellingPlanId, detail.rechargePlanId];
    var roots = collectRoots();
    var control = roots.length ? findFrequencyControl(roots, ids, detail.frequency) : null;

    if (control) {
      withScrollLock(function () {
        applyControl(control);
      });
      // Let the widget's own handler run first, then verify.
      setTimeout(function () {
        if (token !== syncToken) return;
        alignFormSellingPlan(root, detail.sellingPlanId);
        refreshCosts(root);
      }, 150);

      root.dispatchEvent(
        new CustomEvent('pwd:frequency-synced', { bubbles: true, detail: detail })
      );
      return;
    }

    if (attempt < 20) {
      setTimeout(function () {
        syncFrequency(root, detail, attempt + 1, token);
      }, 250);
      return;
    }

    // Widget never exposed a matching control — still submit the right plan.
    alignFormSellingPlan(root, detail.sellingPlanId);
    console.warn(
      '[weight-dosing] No Recharge control found for ' +
        detail.frequency +
        ' (selling plan ' +
        detail.sellingPlanId +
        '). Falling back to the form value.'
    );
  }

  function selectBand(root, band, userInitiated, syncPlanOnLoad) {
    Array.prototype.slice.call(root.querySelectorAll('[data-pwd-weight]')).forEach(function (button) {
      var isActive = button.getAttribute('data-band') === band;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    Array.prototype.slice.call(root.querySelectorAll('[data-pwd-panel]')).forEach(function (panel) {
      var isActive = panel.getAttribute('data-band') === band;
      panel.classList.toggle('active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    var panel = activePanel(root);
    if (!panel) return;

    setQuantity(root, panel.getAttribute('data-bags'));

    var detail = {
      band: band,
      bags: parseInt(panel.getAttribute('data-bags'), 10) || 1,
      frequency: panel.getAttribute('data-frequency') || 'none',
      sellingPlanId: panel.getAttribute('data-selling-plan-id') || '',
      rechargePlanId: panel.getAttribute('data-recharge-plan-id') || '',
      planName: panel.getAttribute('data-plan-name') || '',
      userInitiated: !!userInitiated,
    };

    root.dispatchEvent(new CustomEvent('pwd:band-change', { bubbles: true, detail: detail }));

    // Only follow an explicit weight choice, so we never overwrite a frequency
    // the customer picked before the page had a band selected for them.
    if (userInitiated || syncPlanOnLoad) {
      syncToken += 1;
      syncFrequency(root, detail, 0, syncToken);
    }
  }

  /**
   * Proxy the tap to the theme's own add-to-cart button. If that button is
   * hidden on mobile its loading state is invisible, so mirror it onto ours.
   */
  function addToCart(root, moduleButton) {
    var panel = activePanel(root);
    if (panel) setQuantity(root, panel.getAttribute('data-bags'));

    var nativeButton = getNativeAddButton(root);

    if (!nativeButton) {
      // Last resort: submit the form directly so the tap is never a dead end.
      var form = getForm(root);
      if (form) {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }
      return;
    }

    // The theme's handler returns silently when aria-disabled is still "true"
    // (product-form.js), and it leaves that attribute behind whenever an add
    // errors. A leftover flag would make every later tap do nothing at all, so
    // only respect it while the button is genuinely mid-request.
    var isLoading = nativeButton.classList.contains('loading');
    if (!isLoading && nativeButton.getAttribute('aria-disabled') === 'true') {
      nativeButton.removeAttribute('aria-disabled');
    }

    if (nativeButton.disabled) {
      showError(root, 'This product is currently unavailable.');
      return;
    }

    if (isLoading) return; // A request is already in flight; let it finish.

    clearError(root);
    moduleButton.classList.add('is-loading');

    var settled = false;
    var observer = null;
    var enteredLoading = false;

    var clear = function () {
      if (settled) return;
      settled = true;
      if (observer) observer.disconnect();
      moduleButton.classList.remove('is-loading');
      // If the theme rendered an error, repeat it inside the module — its own
      // error area sits with the theme button, which may be off screen.
      var message = themeErrorMessage(root);
      if (message) showError(root, message);
    };

    if (window.MutationObserver) {
      observer = new MutationObserver(function () {
        if (nativeButton.classList.contains('loading')) {
          enteredLoading = true;
        } else if (enteredLoading) {
          clear();
        }
      });
      observer.observe(nativeButton, { attributes: true, attributeFilter: ['class'] });
    }

    // .click() does not move focus, so this cannot scroll the page.
    nativeButton.click();

    // If the theme never started a request, don't sit in a loading state that
    // blocks further taps — release quickly and say something.
    setTimeout(function () {
      if (enteredLoading || settled) return;
      clear();
      if (!themeErrorMessage(root)) {
        showError(root, 'Sorry, that didn\'t go through. Please try again.');
        console.warn('[weight-dosing] The theme add-to-cart did not start a request.');
      }
    }, 500);

    setTimeout(clear, 8000); // Backstop against a request that never resolves.
  }

  /** Whatever error the theme's product form is currently showing, if any. */
  function themeErrorMessage(root) {
    var form = getForm(root);
    var wrapper = form && form.closest('product-form');
    var el = wrapper && wrapper.querySelector('.product-form__error-message');
    if (!el || (el.closest('[hidden]') && el.closest('[hidden]').hasAttribute('hidden'))) return '';
    return (el.textContent || '').trim();
  }

  function showError(root, message) {
    var target = root.querySelector('[data-pwd-error]');
    if (!target) return;
    target.textContent = message;
    target.removeAttribute('hidden');
  }

  function clearError(root) {
    var target = root.querySelector('[data-pwd-error]');
    if (!target) return;
    target.textContent = '';
    target.setAttribute('hidden', '');
  }

  function init(root) {
    if (root.hasAttribute('data-pwd-ready')) return;
    root.setAttribute('data-pwd-ready', '');

    root.addEventListener('click', function (event) {
      if (!event.target.closest) return;

      var weightButton = event.target.closest('[data-pwd-weight]');
      if (weightButton && root.contains(weightButton)) {
        selectBand(root, weightButton.getAttribute('data-band'), true);
        return;
      }

      var addButton = event.target.closest('[data-pwd-add]');
      if (addButton && root.contains(addButton)) {
        addToCart(root, addButton);
      }
    });

    // Apply the default band, but only on mobile where the module is visible —
    // otherwise a desktop visitor would silently get a quantity they never asked
    // for. Passing userInitiated: false keeps the frequency sync out of it unless
    // "sync on page load" is switched on.
    //
    // Also point the Recharge widget at the default band's recommended frequency,
    // so the card doesn't say "1 bag every 2 months" while the widget still has
    // "1 month" selected. If the shopper taps the widget before that lands, the
    // pending sync is cancelled so their own choice wins.
    var panel = activePanel(root);
    if (panel && isMobile()) {
      selectBand(root, panel.getAttribute('data-band'), false, true);

      var cancelLoadSync = function (event) {
        var path = event.composedPath ? event.composedPath() : [];
        for (var i = 0; i < path.length; i++) {
          if (path[i].tagName === 'RECHARGE-SUBSCRIPTION-WIDGET') {
            syncToken += 1;
            document.removeEventListener('click', cancelLoadSync, true);
            return;
          }
        }
      };
      document.addEventListener('click', cancelLoadSync, true);
      setTimeout(function () {
        document.removeEventListener('click', cancelLoadSync, true);
      }, 6000);
    }

    watchPurchaseOption(root);
  }

  function initAll() {
    Array.prototype.slice.call(document.querySelectorAll('[data-pwd-module]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders the section on setting changes.
  document.addEventListener('shopify:section:load', initAll);

  /** QA helper: run pwdDebug() in the console to see what the module resolved. */
  window.pwdDebug = function () {
    var root = document.querySelector('[data-pwd-module]');
    if (!root) return 'No dosing module on this page.';

    var panel = activePanel(root);
    var form = getForm(root);
    var input = form && form.querySelector('[name="selling_plan"]');
    var quantity = getQuantityInput(root);

    return {
      purchaseOption: currentPurchaseKey(root),
      sellingPlanInput: input ? input.value || '(empty = one-time)' : '(no input on form)',
      checkedInWidget: checkedControls().map(function (el) {
        return el.tagName.toLowerCase() + ' value="' + (el.value || '') + '" — ' + controlLabel(el);
      }),
      band: panel && panel.getAttribute('data-band'),
      bags: panel && panel.getAttribute('data-bags'),
      quantityField: quantity ? quantity.value : '(none)',
      recommendedFrequency: panel && panel.getAttribute('data-frequency'),
      costOptions: panel && panel.getAttribute('data-cost-map'),
      shownCost: panel && panel.querySelector('[data-pwd-cost]').textContent.trim(),
    };
  };
})();
